// components/MomentFooter.jsx — Anamoria SPA
// v0.7 — T-5 (2026-09-13): data-tour anchors on Add a memory + Invite for the onboarding
//   tour (inert strings, zero runtime cost). No logic/CSS change.
// v0.6 — settings overlay-on-feed (no flag): the Settings and Support gears always
//   navigate WITH state.backgroundLocation (current location) so /settings renders as
//   an overlay over the feed. (This footer is the sole Settings entry per D8.)
// v0.4 — DF-7 feed chrome: Add a memory dropped the sage pill -> plain .btn (matches
//   Invite/Settings); icon = plus-in-card (echoes the Add a memory button), so it no
//   longer collides with the header "new space" bare +.
// v0.2 — feed_redesign_v2: additive `redesign` prop → .navV2 (footer matches
//   header surface). Falsy ⇒ byte-identical. (July 27, 2026)
// v0.1 — 2.1c flag-gated footer: Add a memory · Invite · Settings · Support
//   (July 19, 2026)
//
// Executes moment-phase2-capture-plan-v0.4.md step 2.1c per D8 (closed
// 2026-07-19). Rendered by SpacePage ONLY when
// appState.flags?.moments_enabled === true (Aspera canary); all other users
// keep BottomNav v1.4, which this file deliberately does not touch
// (non-Aspera byte-identical by construction — Parallel Change).
//
// Targets (Q-2.1c-1..3 closed by Sophie 2026-07-19):
//   Add a memory → /spaces/{id}/add            (D7 route, AddMemoryPage v0.2)
//   Invite       → /spaces/{id}/invite          (existing BottomNav target)
//   Settings     → /settings?from={spaceId}     (sole Settings entry per D8;
//                                                v3.12 lands on category list)
//   Support      → /settings?from={spaceId}&section=need-help
//                  (distress off-ramp; visually distinct per D8 — divider +
//                  tinted icon, calm emphasis, no alarm color)
//
// Labels ratified in the 2.0-A brand-voice pass (capture copy v0.2). No new
// user-facing strings introduced here.
//
// Props:
//   spaceId — current space UUID (used for navigation)

import { useNavigate, useLocation } from 'react-router-dom';
import { InviteIcon } from './BrandIcons';
import styles from './MomentFooter.module.css';

/* ─── Inline SVG icons ─── */

function AddIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1.5" y="3" width="21" height="18" rx="3" />
      <line x1="12" y1="7.5" x2="12" y2="16.5" />
      <line x1="7.5" y1="12" x2="16.5" y2="12" />
    </svg>
  );
}

// Same gear path as SpacePage SettingsIcon (v2.21) — duplicated here so this
// component stays self-contained and SpacePage's inline icon set is untouched.
function GearIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function SupportIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export default function MomentFooter({ spaceId, redesign = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  // v0.6: carry backgroundLocation so /settings opens as an overlay over this feed.
  const goSettings = (to) => navigate(to, { state: { backgroundLocation: location } });

  return (
    <nav className={`${styles.nav}${redesign ? ` ${styles.navV2}` : ''}`} aria-label="Main navigation">

      {/* Add a memory — matches the other nav items (v1.3: dropped the sage pill) */}
      <button
        className={`${styles.btn} ${styles.btnAdd}`}
        onClick={() => navigate(`/spaces/${spaceId}/add`)}
        title="Add a memory"
        aria-label="Add a memory"
        data-tour="add-memory"
      >
        <span className={styles.icon}>
          <AddIcon />
        </span>
        <span className={styles.label}>Add a memory</span>
      </button>

      {/* Invite */}
      <button
        className={styles.btn}
        onClick={() => navigate(`/spaces/${spaceId}/invite`)}
        title="Invite someone"
        aria-label="Invite someone"
        data-tour="invite-btn"
      >
        <span className={styles.icon}>
          <InviteIcon />
        </span>
        <span className={styles.label}>Invite</span>
      </button>

      {/* Settings — sole Settings entry per D8; ?from returns back-nav to this space */}
      <button
        className={styles.btn}
        onClick={() => goSettings(`/settings?from=${spaceId}`)}
        title="Settings"
        aria-label="Settings"
      >
        <span className={styles.icon}>
          <GearIcon />
        </span>
        <span className={styles.label}>Settings</span>
      </button>

      {/* Support — distress off-ramp, visually distinct (divider + tinted icon) */}
      <button
        className={`${styles.btn} ${styles.btnSupport}`}
        onClick={() => goSettings(`/settings?from=${spaceId}&section=need-help`)}
        title="Support"
        aria-label="Support"
      >
        <span className={`${styles.icon} ${styles.iconSupport}`}>
          <SupportIcon />
        </span>
        <span className={styles.label}>Support</span>
      </button>

    </nav>
  );
}
